import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import dayjs from "dayjs";

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontSize: 10,
    fontFamily: "Helvetica",
    color: "#111",
  },

  header: {
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingBottom: 10,
    marginBottom: 14,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  brand: { fontSize: 14, fontWeight: 700 },
  subtitle: { fontSize: 10, color: "#374151", marginTop: 3 },
  metaBox: { textAlign: "right" },
  meta: { fontSize: 10, color: "#111" },
  metaMuted: { fontSize: 9, color: "#6B7280", marginTop: 2 },

  summaryRow: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 12,
  },
  summaryCard: {
    flex: 1,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    padding: 10,
  },
  summaryLabel: { fontSize: 9, color: "#6B7280" },
  summaryValue: { fontSize: 14, fontWeight: 700, marginTop: 3 },

  table: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    overflow: "hidden",
  },
  tHead: {
    flexDirection: "row",
    backgroundColor: "#F3F4F6",
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  tRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#F1F5F9",
  },

  th: { fontSize: 9, fontWeight: 700, color: "#111" },
  td: { fontSize: 9, color: "#111" },
  muted: { color: "#6B7280" },

  // ✅ Updated columns (A4 safe)
  colDate: { width: "10%" },
  colTitle: { width: "26%" },
  colReceiver: { width: "16%" },
  colQty: { width: "7%", textAlign: "right" },
  colMethod: { width: "12%" },
  colType: { width: "9%" },
  colAmount: { width: "10%", textAlign: "right" },
  colPaid: { width: "5%", textAlign: "right" },
  colRem: { width: "5%", textAlign: "right" },

  footer: {
    position: "absolute",
    left: 28,
    right: 28,
    bottom: 18,
    flexDirection: "row",
    justifyContent: "space-between",
    color: "#6B7280",
    fontSize: 9,
  },
});

const money = (n) => Number(n || 0).toLocaleString();

const methodLabel = (m) => {
  const map = {
    cash: "Cash",
    online: "Online",
    cheque: "Cheque",
    bank_transfer: "Bank",
    card: "Card",
    other: "Other",
  };
  return map[m] || "Cash";
};

// ✅ truncate helper for description/title column
const truncate = (str = "", max = 42) => {
  const s = String(str || "");
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + "…";
};

export default function StatementPDF({ projectName, from, to, rows = [] }) {
  const totalQty = rows.reduce((s, r) => s + Number(r.quantity || 0), 0);
  const totalAmount = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const totalPaid = rows.reduce((s, r) => s + Number(r.paidAmount || 0), 0);
  const totalRemaining = Math.max(totalAmount - totalPaid, 0);

  const genDate = dayjs().format("DD MMM YYYY, hh:mm A");

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View>
            <Text style={styles.brand}>HB Expense Statement</Text>
            <Text style={styles.subtitle}>
              Project: {projectName || "—"} • Period: {from} to {to}
            </Text>
          </View>
          <View style={styles.metaBox}>
            <Text style={styles.meta}>Generated: {genDate}</Text>
            <Text style={styles.metaMuted}>Hafiz Brothers Builders & Developers</Text>
          </View>
        </View>

        {/* Summary */}
        <View style={styles.summaryRow}>
          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>Total Quantity</Text>
            <Text style={styles.summaryValue}>{money(totalQty)}</Text>
          </View>
          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>Total Amount</Text>
            <Text style={styles.summaryValue}>{money(totalAmount)}</Text>
          </View>
          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>Total Paid</Text>
            <Text style={styles.summaryValue}>{money(totalPaid)}</Text>
          </View>
          <View style={styles.summaryCard}>
            <Text style={styles.summaryLabel}>Total Remaining</Text>
            <Text style={styles.summaryValue}>{money(totalRemaining)}</Text>
          </View>
        </View>

        {/* Table */}
        <View style={styles.table}>
          <View style={styles.tHead}>
            <Text style={[styles.th, styles.colDate]}>Date</Text>
            <Text style={[styles.th, styles.colTitle]}>Title / Description</Text>
            <Text style={[styles.th, styles.colReceiver]}>Receiver</Text>
            <Text style={[styles.th, styles.colQty]}>Qty</Text>
            <Text style={[styles.th, styles.colMethod]}>Method</Text>
            <Text style={[styles.th, styles.colType]}>Type</Text>
            <Text style={[styles.th, styles.colAmount]}>Amount</Text>
            <Text style={[styles.th, styles.colPaid]}>Paid</Text>
            <Text style={[styles.th, styles.colRem]}>Rem.</Text>
          </View>

          {rows.map((r, idx) => {
            const amt = Number(r.amount || 0);
            const paid = Number(r.paidAmount || 0);
            const rem = Math.max(amt - paid, 0);
            const qty = Number(r.quantity || 0);

            const title = truncate(r.title || "—", 28);
            const desc = r.description ? truncate(r.description, 36) : "";

            return (
              <View key={r._id || idx} style={styles.tRow}>
                <Text style={[styles.td, styles.colDate]}>
                  {dayjs(r.date).format("DD MMM")}
                </Text>

                <Text style={[styles.td, styles.colTitle]}>
                  {title}
                  {desc ? <Text style={styles.muted}> • {desc}</Text> : null}
                </Text>

                <Text style={[styles.td, styles.colReceiver]}>
                  {truncate(r.receiverName || "—", 18)}
                </Text>

                <Text style={[styles.td, styles.colQty]}>{money(qty)}</Text>

                <Text style={[styles.td, styles.colMethod, styles.muted]}>
                  {methodLabel(r.paymentMethod)}
                </Text>

                <Text style={[styles.td, styles.colType, styles.muted]}>
                  {r.paymentType === "partial" ? "Partial" : "Full"}
                </Text>

                <Text style={[styles.td, styles.colAmount]}>{money(amt)}</Text>
                <Text style={[styles.td, styles.colPaid]}>{money(paid)}</Text>
                <Text style={[styles.td, styles.colRem]}>{money(rem)}</Text>
              </View>
            );
          })}
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text>HB Height • Statement</Text>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`} fixed />
        </View>
      </Page>
    </Document>
  );
}
